import { FaCheck } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Button } from "components/buttonV2";

interface IProps {
  sending: boolean;
  sendProgress: string;
  sendResults: {
    total: number;
    completed: number;
    successful: number;
    failed: number;
  };
  backButton: () => void;
  sendType?: string;
}

const Success = ({
  sending,
  sendProgress,
  sendResults,
  backButton,
  sendType,
}: IProps) => {
  const { t } = useTranslation();
  const tBase = "views.service-plan-dashboard.components.Success";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const history = useHistory();

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="w-full max-w-md mb-8">
        <div className={`border rounded-lg p-6 transition-all duration-500 ${
          sending 
            ? "bg-blue-50 border-blue-200" 
            : "bg-green-50 border-green-200"
        }`}>
          <div className="text-center mb-4">
            {/* Icon transitions from spinner to check mark */}
            <div className="h-12 w-12 mx-auto mb-4 flex items-center justify-center">
              {sending ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              ) : (
                <FaCheck size={32} className="text-green-500" />
              )}
            </div>
            
            {/* Title transitions */}
            <h3 className={`text-lg font-semibold transition-colors duration-300 ${
              sending ? "text-blue-700" : "text-green-700"
            }`}>
              {sending ? (
                sendType === "contract" ? tr("Sending Plans...") : tr("Adding Plans...")
              ) : (
                tr("Success")
              )}
            </h3>
          </div>
          
          {/* Progress text (only show while sending) */}
          {sending && (
            <div className="text-sm text-blue-700 mb-4 text-center">
              {sendProgress || tr("Processing...")}
            </div>
          )}
          
          {/* Success message (only show when completed) */}
          {!sending && (
            <p className="text-lg text-green-700 mb-4 text-center">
              {sendType === "contract" ?
                tr("Your contracts have been successfully sent!") :
                tr("Your plans have been successfully added!")}
            </p>
          )}
          
          {/* Results section - shows during and after */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>{tr("Successful")}:</span>
              <span className="text-green-600 font-medium">{sendResults.successful}</span>
            </div>

              <div className="flex justify-between">
                <span>{tr("Failed")}:</span>
                <span className="text-red-600 font-medium">{sendResults.failed}</span>
              </div>

            <div className="flex justify-between font-semibold border-t pt-2">
              <span>{sending ? tr("Progress") : tr("Total processed")}:</span>
              <span>{sending ? `${sendResults.completed}/${sendResults.total}` : sendResults.total}</span>
            </div>
            
            {/* Progress bar (only show while sending) */}
            {sending && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(sendResults.completed / sendResults.total) * 100}%` 
                  }}
                ></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons (only show when completed) */}
      {!sending && (
        <div className="flex flex-col items-center space-y-2">
          <Button
            onClick={() => {
              history.push("/service-plan-dashboard");
            }}
          >
            {tr("Return to Dashboard")}
          </Button>
          <Button
            type="secondary"
            onClick={() => {
              backButton();
            }}
          >
            {tr("Add More Plans")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Success;